import React, { useState, useEffect } from "react";
import {
  ArrowUpRight,
  Clock,
  CheckCircle,
  AlertCircle,
  Wallet,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Send,
  CreditCard,
  TrendingUp,
  ArrowLeft,
  Copy,
  Filter,
  Calendar,
  Download,
  ExternalLink,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { API_BASE_URL } from "../../utils/apiConfig";
import { exportTransactionsToExcel } from "../../utils/excelExport";
import Permission from "../Permission";
import usePermissions from "../../hooks/usePermissions";
import TwoFactorAuth from "../TwoFactorAuth";
import { fetchUserWallet, selectAvailableBalance } from "../../store/slices/walletSlice";

const WithdrawRequestsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasPermission, canPerformAction, isSubUser, userRole } = usePermissions();
  const [amount, setAmount] = useState("");
  const [merchantWallet, setMerchantWallet] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [withdrawals, setWithdrawals] = useState([]);
  const [allWithdrawals, setAllWithdrawals] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const ITEMS_PER_PAGE = 10;
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currencyType, setCurrencyType] = useState("USDT-TRC20");
  const [globalTotalRequests, setGlobalTotalRequests] = useState(0);
  const [globalTotalApproved, setGlobalTotalApproved] = useState(0);
  const [globalTotalPending, setGlobalTotalPending] = useState(0);
  const [globalTotalFailed, setGlobalTotalFailed] = useState(0);
  const [globalPendingAmount, setGlobalPendingAmount] = useState(0);
  const [globalFailedAmount, setGlobalFailedAmount] = useState(0);
  const [globalTotalRejected, setGlobalTotalRejected] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processingWithdrawalId, setProcessingWithdrawalId] = useState(null);
  const [rowActionResult, setRowActionResult] = useState(null); // { id, type: "success"|"error", message }

  // 2FA state for withdrawal approval/rejection (Fix 1.1)
  const [showTwoFAModal, setShowTwoFAModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { withdrawal, action }

  // OTP state for payout submission (Fix 1.2-WR)
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpMessage, setOtpMessage] = useState("");

  // Balance from Redux (Fix I)
  const walletData = useSelector(selectAvailableBalance);
  const availableBalance = walletData?.availableBalance || 0;

  // Fetch wallet balance on mount (Fix I)
  useEffect(() => {
    dispatch(fetchUserWallet());
  }, [dispatch]);

  // Initiate approval/rejection — shows confirm dialog then opens 2FA modal (Fix 1.1)
  const initiateAction = (withdrawal, action) => {
    const actionText = action === "approve" ? "approve" : "reject";
    const confirmMessage = `Are you sure you want to ${actionText} this payout of ${withdrawal.amount} USDT?`;
    if (!window.confirm(confirmMessage)) return;

    setPendingAction({ withdrawal, action });
    setShowTwoFAModal(true);
  };

  // Auto-dismiss global messages after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Auto-dismiss per-row action result after 4 seconds
  useEffect(() => {
    if (rowActionResult) {
      const timer = setTimeout(() => setRowActionResult(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [rowActionResult]);

  // Execute the PATCH after 2FA verification succeeds (Fix 1.1)
  const executeAction = async () => {
    setShowTwoFAModal(false);
    if (!pendingAction) return;

    const { withdrawal, action } = pendingAction;
    const actionText = action === "approve" ? "approved" : "rejected";

    setProcessingWithdrawalId(withdrawal._id);
    setRowActionResult(null);
    try {
      const response = await fetch(`${API_BASE_URL}/withdrawal/${withdrawal._id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action,
          adminNotes: `${action === "approve" ? "Approved" : "Rejected"} by merchant`,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || `Failed to ${action} withdrawal`);
      }
      setRowActionResult({ id: withdrawal._id, type: "success", message: `Payout ${actionText} successfully` });
      fetchAllWithdrawals();
      dispatch(fetchUserWallet()); // Refresh balance after action
    } catch (err) {
      setRowActionResult({ id: withdrawal._id, type: "error", message: err.message || `Failed to ${action} withdrawal` });
    } finally {
      setProcessingWithdrawalId(null);
      setPendingAction(null);
    }
  };

  // Filter states
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState(""); // "", "oldest", "newest"

  // Filter withdrawals based on date and status
  const filterWithdrawals = (data) => {
    // Note: Refund routes are disabled in app.js. Records with type="refund" are
    // mislabeled withdrawal requests (old frontend bug, fixed in commit 5b53c70).
    // No filter needed — show all records so merchants can approve/reject them.
    let filtered = [...data];

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((w) => w.status === statusFilter);
    }

    // Date filter
    if (dateFilter !== "all") {
      const now = new Date();

      filtered = filtered.filter((w) => {
        const wDate = new Date(w.createdAt);

        switch (dateFilter) {
          case "today":
            return wDate.toDateString() === now.toDateString();
          case "week":
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return wDate >= weekAgo;
          case "month":
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return wDate >= monthAgo;
          case "custom":
            if (startDate && endDate) {
              const start = new Date(startDate);
              const end = new Date(endDate);
              end.setHours(23, 59, 59, 999); // Include the entire end date
              return wDate >= start && wDate <= end;
            }
            return true;
          default:
            return true;
        }
      });
    }

    // Sorting by createdAt
    const sorted = [...filtered];
    if (sortBy === "oldest") {
      sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    } else if (sortBy === "newest") {
      sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    return sorted;
  };

  // Apply client-side pagination to filtered data
  const applyPagination = (filteredData) => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedData = filteredData.slice(startIndex, endIndex);
    const totalPagesCalculated = Math.ceil(
      filteredData.length / ITEMS_PER_PAGE
    );

    setWithdrawals(paginatedData);
    setTotalPages(totalPagesCalculated);
  };

  // Fetch ALL withdrawals from all pages (Fix 1.4a: safety cap at 10 pages)
  const fetchAllWithdrawals = async () => {
    setLoading(true);
    try {
      let pageNum = 1;
      let allData = [];
      let totalPagesLocal = 1;

      do {
        const response = await fetch(
          `${API_BASE_URL}/withdrawal/my-withdrawals?page=${pageNum}&limit=50`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();

        if (response.ok) {
          allData = [...allData, ...data.data];
          totalPagesLocal = data.totalPages;
          pageNum++;
        } else {
          setError(data.message);
          break;
        }
      } while (pageNum <= totalPagesLocal && pageNum <= 10);

      // Store all withdrawals
      setAllWithdrawals(allData);

      // Apply filters and pagination
      const filteredData = filterWithdrawals(allData);
      applyPagination(filteredData);
    } catch (err) {
      setError("An error occurred while fetching withdrawals.");
    } finally {
      setLoading(false);
    }
  };

  // Handle page change for client-side pagination
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Apply filters when filter state changes
  useEffect(() => {
    if (allWithdrawals.length > 0) {
      const filteredData = filterWithdrawals(allWithdrawals);
      setPage(1); // Reset to first page when filters change
      applyPagination(filteredData);
    }
  }, [statusFilter, dateFilter, startDate, endDate, sortBy]);

  // Apply pagination when page changes
  useEffect(() => {
    if (allWithdrawals.length > 0) {
      const filteredData = filterWithdrawals(allWithdrawals);
      applyPagination(filteredData);
    }
  }, [page]);

  // Initial data fetch
  useEffect(() => {
    fetchAllWithdrawals();
  }, []);

  const fetchAllWithdrawalsForStats = async () => {
    try {
      // Use the already fetched data for stats calculation
      const allData = allWithdrawals.length > 0 ? allWithdrawals : [];

      if (allData.length === 0) return;

      // Apply current filters for stats
      const filteredData = filterWithdrawals(allData);

      // Filter by status
      const completedItems = filteredData.filter(
        (w) => w.status === "completed"
      );
      const pendingItems = filteredData.filter((w) => w.status === "pending");
      const failedItems = filteredData.filter((w) => w.status === "failed");
      const rejectedItems = filteredData.filter((w) => w.status === "rejected");

      // Aggregate
      setGlobalTotalRequests(filteredData.length);
      setGlobalTotalApproved(
        completedItems.reduce((sum, w) => sum + parseFloat(w.amount || 0), 0)
      );
      setGlobalTotalPending(pendingItems.length);
      setGlobalTotalFailed(failedItems.length);

      setGlobalPendingAmount(
        pendingItems.reduce((sum, w) => sum + parseFloat(w.amount || 0), 0)
      );
      setGlobalFailedAmount(
        failedItems.reduce((sum, w) => sum + parseFloat(w.amount || 0), 0)
      );
      setGlobalTotalRejected(
        rejectedItems.reduce((sum, w) => sum + parseFloat(w.amount || 0), 0)
      );
    } catch (err) {
      console.error("Error calculating stats:", err);
    }
  };

  useEffect(() => {
    fetchAllWithdrawalsForStats();
  }, [allWithdrawals, statusFilter, dateFilter, startDate, endDate]);

  // Handle form submit to initiate withdrawal (Fix 1.2-WR: OTP flow)
  const handleSubmit = async () => {
    setError("");
    setSuccessMessage("");
    setIsSubmitting(true);

    if (!amount || !merchantWallet) {
      setError("Please fill in all fields");
      setIsSubmitting(false);
      return;
    }

    // Balance validation (Fix I)
    if (parseFloat(amount) > availableBalance) {
      setError("Insufficient balance");
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = {
        amount: parseFloat(amount),
        merchantWallet: merchantWallet,
        currencyType: currencyType,
      };

      // Include OTP code if available
      if (otpCode) {
        payload.twoFACode = otpCode;
      }

      const response = await fetch(`${API_BASE_URL}/withdrawal`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        // Check if backend requires 2FA/OTP
        if (data.data?.requires2FA || data.data?.requiresOTP) {
          setOtpMessage(data.message || "Please enter the verification code sent to your email.");
          setShowOTPModal(true);
          setOtpCode("");
          setIsSubmitting(false);
          return;
        }

        setSuccessMessage(
          data.message || "Payout request submitted successfully."
        );
        setAmount("");
        setMerchantWallet("");
        setShowOTPModal(false);
        setOtpCode("");
        setOtpMessage("");
        fetchAllWithdrawals();
        dispatch(fetchUserWallet()); // Refresh balance
      } else {
        // Check for 2FA required response in error
        if (data.data?.requires2FA || data.data?.requiresOTP) {
          setOtpMessage(data.message || "Please enter the verification code.");
          setShowOTPModal(true);
          setOtpCode("");
          setIsSubmitting(false);
          return;
        }

        // Handle invalid 2FA code
        if (data.error === "INVALID_2FA_CODE" || data.message?.toLowerCase().includes("invalid") && data.message?.toLowerCase().includes("code")) {
          setOtpMessage("Invalid code. Please try again.");
          setOtpCode("");
          setIsSubmitting(false);
          return;
        }

        setError(
          data.message || "Something went wrong while submitting payout."
        );
      }
    } catch (err) {
      setError("An error occurred while processing your request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-700 border-green-300";
      case "pending":
        return "bg-yellow-100 text-yellow-700 border-yellow-300";
      case "executing":
        return "bg-blue-100 text-blue-700 border-blue-300";
      case "rejected":
        return "bg-red-100 text-red-700 border-red-300";
      case "failed":
        return "bg-red-100 text-red-700 border-red-300";
      default:
        return "bg-gray-100 text-gray-600 border-gray-300";
    }
  };

  // Get explorer URL for a tx hash based on currency type
  const getExplorerUrl = (txHash, currency) => {
    if (!txHash) return null;
    if (currency && currency.includes("TRC20")) {
      return `https://tronscan.org/#/transaction/${txHash}`;
    }
    return `https://etherscan.io/tx/${txHash}`;
  };

  // Refresh data
  const refreshData = () => {
    setIsRefreshing(true);
    fetchAllWithdrawals();
    dispatch(fetchUserWallet());
    setTimeout(() => setIsRefreshing(false), 1500);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Export filtered withdrawals to Excel
  const handleExportWithdrawals = () => {
    const filteredData = filterWithdrawals(allWithdrawals);

    if (filteredData.length === 0) {
      alert('No withdrawal requests to export');
      return;
    }

    if (!window.confirm(`This will export ALL ${filteredData.length} withdrawal requests from all pages. Continue?`)) {
      return;
    }

    const filters = {
      status: statusFilter,
      dateFilter: dateFilter,
      sortBy: sortBy,
      startDate: startDate,
      endDate: endDate
    };

    const result = exportTransactionsToExcel(filteredData, 'withdrawal', filters);

    if (result.success) {
      alert(result.message);
    } else {
      alert(result.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div className="mb-4 md:mb-0 ">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg hover:bg-blue-100 transition-colors"
                title="Back"
              >
                <ArrowLeft className="w-5 h-5 text-blue-600" />
              </button>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
                Payout Requests
              </h2>
            </div>
            <p className="text-gray-600 text-sm md:text-base">
              Submit new Payout requests and track their status
            </p>
          </div>
          <div className="flex flex-row md:items-center md:justify-between mb-8 gap-4 md:gap-8">
            <button
              onClick={refreshData}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg font-medium transition-all duration-300 inline-flex items-center justify-center space-x-2 shadow-lg hover:shadow-blue-500/25 w-auto"
            >
              <RefreshCw
                className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
              />
              <span className="hidden sm:inline">Refresh</span>
            </button>

            <button
              onClick={handleExportWithdrawals}
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg font-medium transition-all duration-300 inline-flex items-center justify-center space-x-2 shadow-lg hover:shadow-green-500/25 w-auto"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg font-medium transition-all duration-300 inline-flex items-center justify-center space-x-2 shadow-lg hover:shadow-purple-500/25 w-auto"
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filters</span>
            </button>
          </div>
        </div>

        {/* Filters Section */}
        {showFilters && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-lg mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Status Filter — simplified to actual payout outcomes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="executing">Executing</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Rejected</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              {/* Date Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Date
                </label>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {/* Sort By */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sort by
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Default</option>
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                </select>
              </div>

              {/* Custom Date Range */}
              {dateFilter === "custom" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Clear Filters Button */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setStatusFilter("all");
                  setDateFilter("all");
                  setStartDate("");
                  setEndDate("");
                  setSortBy("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}

        {/* Success and Error Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <span className="text-green-700">{successMessage}</span>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Payout Form */}
          <Permission
            permission="approved-withdrawals"
            fallback={
              <div className="lg:col-span-1 bg-red-50 border border-red-200 rounded-xl p-6">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-red-800 mb-2">
                    Access Restricted
                  </h3>
                  <p className="text-red-600 mb-3">
                    You don't have permission to create withdrawal requests.
                  </p>
                  <div className="bg-red-100 rounded-lg p-3 text-left">
                    <p className="text-sm text-red-700">
                      <strong>Your Role:</strong> {userRole?.replace('_', ' ').toUpperCase()}
                    </p>
                    <p className="text-sm text-red-700 mt-1">
                      <strong>Required Permission:</strong> approved-withdrawals
                    </p>
                    {userRole === 'operation' && (
                      <p className="text-sm text-red-700 mt-2">
                        You can view withdrawal requests but cannot create them.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            }
          >
            <div className="lg:col-span-1 bg-white border border-gray-200 rounded-xl p-6 shadow-xl">
              {/* Available Balance Display (Fix I) */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="text-sm text-blue-700">Available Balance</div>
                <div className="text-xl font-bold text-blue-800">
                  {availableBalance.toFixed(2)} USDT
                </div>
              </div>

              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center shadow-lg">
                  <Send className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    New Payout
                  </h3>
                  <p className="text-sm text-gray-600">Submit a Payout request</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label
                    htmlFor="currencyType"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Currency Type
                  </label>
                  <select
                    id="currencyType"
                    value={currencyType}
                    onChange={(e) => setCurrencyType(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="USDT-TRC20">USDT-TRC20</option>
                    <option value="USDT-ERC20">USDT-ERC20</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="amount"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Amount (USDT)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      id="amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Enter amount"
                      className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                    <CreditCard className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="merchantWallet"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Merchant Wallet Address
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="merchantWallet"
                      value={merchantWallet}
                      onChange={(e) => setMerchantWallet(e.target.value)}
                      placeholder="Enter wallet address"
                      className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                    <Wallet className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-300 shadow-lg flex items-center justify-center space-x-2 ${isSubmitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 hover:shadow-blue-500/25"
                    } text-white`}
                >
                  {isSubmitting ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Submit Payout Request</span>
                    </>
                  )}
                </button>
              </div>

              {/* Quick Stats (Fix G: renamed label) */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                {/* Total & Completed */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-800">
                      {globalTotalRequests}
                    </div>
                    <div className="text-xs text-gray-600">Total Requests</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600">
                      {globalTotalApproved.toFixed(2)} USDT
                    </div>
                    <div className="text-xs text-gray-600">
                      Total Completed Amount
                    </div>
                  </div>
                </div>

                {/* Pending */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-yellow-600">
                      {globalTotalPending}
                    </div>
                    <div className="text-xs text-gray-600">Pending Requests</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-yellow-600">
                      {globalPendingAmount.toFixed(2)} USDT
                    </div>
                    <div className="text-xs text-gray-600">Pending Amount</div>
                  </div>
                </div>
              </div>
            </div>
          </Permission>

          {/* Payout History */}
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-xl">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      Payout History
                    </h3>
                    <p className="text-sm text-gray-600">
                      Track your Payout requests
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <div className="divide-y divide-gray-200 min-h-[400px]">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                    <span className="ml-3 text-gray-600">
                      Loading withdrawals...
                    </span>
                  </div>
                ) : withdrawals.length === 0 ? (
                  /* Fix F: Dynamic empty state message */
                  <div className="flex flex-col items-center justify-center py-12">
                    <Wallet className="w-16 h-16 text-gray-300 mb-4" />
                    <p className="text-gray-500 text-lg">
                      {statusFilter !== "all"
                        ? `No ${statusFilter} payout requests found`
                        : "No payout requests found"}
                    </p>
                    <p className="text-gray-400 text-sm">
                      {statusFilter !== "all"
                        ? "Try changing your filters"
                        : "Submit your first payout request above"}
                    </p>
                  </div>
                ) : (
                  withdrawals.map((withdrawal) => (
                    <div
                      key={withdrawal._id}
                      className="p-4 hover:bg-gray-50 transition-colors duration-200 group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <ArrowUpRight className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="flex items-center space-x-2 mb-1 flex-wrap gap-1">
                              <span className="text-gray-800 font-medium">
                                Payout Request
                              </span>
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                                  withdrawal.status
                                )}`}
                              >
                                {withdrawal.status.toUpperCase()}
                              </span>
                              {/* Fix A: Currency type badge */}
                              {withdrawal.currencyType && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                  {withdrawal.currencyType}
                                </span>
                              )}
                            </div>
                            {/* Wallet address */}
                            <div className="flex items-center space-x-2">
                              <span className="text-gray-500 text-sm">
                                {withdrawal.clientWallet.slice(0, 12)}...
                                {withdrawal.clientWallet.slice(-6)}
                              </span>
                              <button
                                onClick={() =>
                                  copyToClipboard(withdrawal.clientWallet)
                                }
                                className="text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                            {/* Fix 1.5: Blockchain TX Hash */}
                            {withdrawal.blockchainTxHash && (
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="text-gray-400 text-xs">TX:</span>
                                <span className="text-gray-500 text-xs font-mono">
                                  {withdrawal.blockchainTxHash.slice(0, 10)}...
                                  {withdrawal.blockchainTxHash.slice(-6)}
                                </span>
                                <button
                                  onClick={() => copyToClipboard(withdrawal.blockchainTxHash)}
                                  className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                                <a
                                  href={getExplorerUrl(withdrawal.blockchainTxHash, withdrawal.currencyType)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 hover:text-blue-700 transition-colors"
                                  title="View on blockchain explorer"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          {/* Fix B: Consistent amount display (no conditional negative) */}
                          <div
                            className={`text-lg font-semibold ${withdrawal.status === "completed"
                              ? "text-green-600"
                              : withdrawal.status === "rejected"
                                ? "text-red-600"
                                : withdrawal.status === "pending"
                                  ? "text-orange-600"
                                  : withdrawal.status === "executing"
                                    ? "text-blue-600"
                                    : "text-gray-500"
                              }`}
                          >
                            {Math.abs(withdrawal.amount).toFixed(2)} USDT
                          </div>
                          {/* Fix C: Show fee and net amount */}
                          {withdrawal.fee > 0 && (
                            <div className="text-xs text-gray-500">
                              Fee: {withdrawal.fee} USDT | Net: {withdrawal.netAmount?.toFixed(2)} USDT
                            </div>
                          )}
                          <div className="text-sm text-gray-500">
                            {new Date(
                              withdrawal.createdAt
                            ).toLocaleDateString()}{" "}
                            {new Date(
                              withdrawal.createdAt
                            ).toLocaleTimeString()}
                          </div>
                          {/* Inline action result feedback */}
                          {rowActionResult && rowActionResult.id === withdrawal._id && (
                            <div className={`mt-2 px-3 py-1.5 rounded-lg text-sm font-medium ${
                              rowActionResult.type === "success"
                                ? "bg-green-50 text-green-700 border border-green-200"
                                : "bg-red-50 text-red-700 border border-red-200"
                            }`}>
                              {rowActionResult.message}
                            </div>
                          )}
                          {/* Approve/Reject buttons for pending withdrawals (Fix 1.1: uses 2FA gate) */}
                          {withdrawal.status === "pending" && !(rowActionResult && rowActionResult.id === withdrawal._id && rowActionResult.type === "success") && (
                            <div className="flex items-center space-x-2 mt-2">
                              {processingWithdrawalId === withdrawal._id ? (
                                <div className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg border border-blue-200">
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                  <span>Processing...</span>
                                </div>
                              ) : (
                                <>
                                  <button
                                    onClick={() => initiateAction(withdrawal, "approve")}
                                    className="flex items-center space-x-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 shadow-sm hover:shadow-md transition-all"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                    <span>Approve</span>
                                  </button>
                                  <button
                                    onClick={() => initiateAction(withdrawal, "reject")}
                                    className="flex items-center space-x-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 shadow-sm hover:shadow-md transition-all"
                                  >
                                    <AlertCircle className="w-4 h-4" />
                                    <span>Reject</span>
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Pagination */}
            {withdrawals.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Previous</span>
                  </button>

                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">
                      Page <span className="font-medium">{page}</span> of{" "}
                      <span className="font-medium">{totalPages}</span>
                    </span>
                  </div>

                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                  >
                    <span>Next</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 2FA Modal for withdrawal approval/rejection (Fix 1.1) */}
      <TwoFactorAuth
        isOpen={showTwoFAModal}
        onClose={() => {
          setShowTwoFAModal(false);
          setPendingAction(null);
        }}
        onSuccess={executeAction}
      />

      {/* OTP Modal for payout submission (Fix 1.2-WR) */}
      {showOTPModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Verification Required
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {otpMessage || "Please enter the verification code to complete your payout request."}
            </p>
            <input
              type="text"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="Enter 6-digit code"
              maxLength={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-widest font-mono mb-4"
              autoFocus
            />
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowOTPModal(false);
                  setOtpCode("");
                  setOtpMessage("");
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={otpCode.length < 6 || isSubmitting}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Verifying..." : "Verify & Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawRequestsPage;
